/**
 * BudgetGauge — spend-vs-ceiling meter for the admin AI daily-budget surface
 * (P0 kit). Three states: under (<threshold), approaching (>=threshold, <100%),
 * over (>=100%). Color flows from --g-success / --g-warn / --g-error, but the
 * state is ALSO named in a pill (never color-only). Exposes `role="meter"` with
 * aria bounds. Money arrives from the backend as a Decimal **string**; the
 * caller parses to a number before passing it in. Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement } from "react";
import { GPill } from "@garden/ui";
import { CHART } from "@/features/admin/_shared/chartTokens";

export type BudgetStatus = "under" | "approaching" | "over";

/** Default "approaching" boundary as a fraction of the ceiling. */
const DEFAULT_APPROACHING = 0.8;

export function budgetStatus(
  spent: number,
  ceiling: number,
  approachingThreshold: number = DEFAULT_APPROACHING,
): BudgetStatus {
  if (ceiling <= 0) return spent > 0 ? "over" : "under";
  const ratio = spent / ceiling;
  if (ratio >= 1) return "over";
  if (ratio >= approachingThreshold) return "approaching";
  return "under";
}

const STATUS_COLOR: Record<BudgetStatus, string> = {
  under: CHART.success,
  approaching: CHART.warn,
  over: CHART.error,
};
const STATUS_TONE: Record<BudgetStatus, "success" | "warn" | "error"> = {
  under: "success",
  approaching: "warn",
  over: "error",
};

export interface BudgetGaugeProps {
  spent: number;
  ceiling: number;
  label: string;
  statusLabels: Record<BudgetStatus, string>;
  remainingLabel?: string;
  approachingThreshold?: number;
  formatValue?: (n: number) => string;
  testid?: string;
}

export function BudgetGauge({
  spent,
  ceiling,
  label,
  statusLabels,
  remainingLabel,
  approachingThreshold = DEFAULT_APPROACHING,
  formatValue = (n) => String(n),
  testid = "budget-gauge",
}: BudgetGaugeProps): ReactElement {
  const status = budgetStatus(spent, ceiling, approachingThreshold);
  const pct = ceiling > 0 ? Math.min(100, Math.max(0, (spent / ceiling) * 100)) : spent > 0 ? 100 : 0;
  const remaining = Math.max(0, ceiling - spent);
  const color = STATUS_COLOR[status];

  return (
    <div data-testid={testid} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span data-testid={`${testid}-status`}>
          <GPill tone={STATUS_TONE[status]} size="sm">
            {statusLabels[status]}
          </GPill>
        </span>
      </div>

      <div
        data-testid={`${testid}-meter`}
        role="meter"
        aria-label={label}
        aria-valuenow={spent}
        aria-valuemin={0}
        aria-valuemax={ceiling}
        className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--g-hairline)]"
      >
        <div
          data-testid={`${testid}-fill`}
          className="h-full rounded-full transition-[width] duration-300 ease-[var(--g-ease)]"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>

      <div className="flex items-center justify-between text-xs tabular-nums text-[var(--g-secondary)]">
        <span>
          <span data-testid={`${testid}-spent`} className="font-semibold text-foreground">
            {formatValue(spent)}
          </span>{" "}
          / <span data-testid={`${testid}-ceiling`}>{formatValue(ceiling)}</span>
        </span>
        {remainingLabel ? (
          <span data-testid={`${testid}-remaining`}>
            {remainingLabel}: {formatValue(remaining)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default BudgetGauge;
